import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar as CalendarIcon, Clock, Loader2, User as UserIcon, MapPin } from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { DateTimeInput } from '@/components/ui/DateTimeInput';

// ─── BookMeetingDrawer ─────────────────────────────────────────────────────
//
// Right-side drawer opened from the Lead Detail Meetings tab.
// UX contract:
//   - Counselor is fixed = lead.assignedToId (no dropdown to switch).
//   - Event picker only shows events published on that counselor's calendar.
//   - Date picker → available time slots for that event+date.
//   - Guest info (name/email/phone) is auto-filled from the lead — backend
//     handles this, this drawer doesn't even display it (keeps the form
//     compact; the lead card is right next to it on Lead Detail anyway).
// ────────────────────────────────────────────────────────────────────────────

interface EventOpt {
  id: string;
  name: string;
  slug: string;
  duration: number;
  color: string;
  location: string | null;
  description: string | null;
  meetingProvider: string | null;
}

interface BookableEventsResp {
  assignedTo: { id: string; name: string } | null;
  // Counselor's own availability timezone — slot times from the API are
  // in this TZ, and we book in this TZ so the counselor's calendar
  // shows the meeting at the expected wall-clock time.
  timezone: string | null;
  events: EventOpt[];
}

function fmt12(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/** Today in the given IANA timezone as YYYY-MM-DD. Falls back to browser local. */
function todayInTz(tz?: string | null): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz || undefined,
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
    // en-CA already returns YYYY-MM-DD
    return parts;
  } catch {
    const d = new Date();
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  }
}

export function BookMeetingDrawer({
  leadId,
  onClose,
  onBooked,
}: {
  leadId: string;
  onClose: () => void;
  onBooked: () => void;
}) {
  const [meta, setMeta] = useState<BookableEventsResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [eventId, setEventId] = useState<string>('');
  // Date is initialised only after we know the counselor's TZ (see the
  // meta-load effect below), so a bare todayInTz() with no arg is a safe
  // browser-local fallback while loading.
  const [date, setDate] = useState<string>(todayInTz());
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 1) Load bookable events on mount. Also snap `date` to counselor's TZ
  //    today — otherwise an admin browsing at 12:15 AM local (when it's
  //    still yesterday in counselor's TZ, or vice-versa) sees a stale day.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/leads/${leadId}/meetings/bookable-events`)
      .then((r) => {
        if (cancelled) return;
        const data = r.data as BookableEventsResp;
        setMeta(data);
        if (data.events.length > 0) setEventId(data.events[0].id);
        setDate(todayInTz(data.timezone));
      })
      .catch((err) => {
        if (!cancelled) toast('error', err?.response?.data?.message || 'Could not load bookable events');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leadId]);

  // 2) When event or date changes, fetch slots. Reuses the public /book
  //    slots endpoint since it already knows how to compute availability
  //    for an (event, date) pair — no need for a separate lead-scoped
  //    slots endpoint.
  useEffect(() => {
    const ev = meta?.events.find((e) => e.id === eventId);
    if (!ev || !meta?.assignedTo?.id || !date) { setSlots([]); return; }
    let cancelled = false;
    setSlotsLoading(true);
    setSlot('');
    api.get(`/book/${meta.assignedTo.id}/${ev.slug}/slots`, { params: { date } })
      .then((r) => { if (!cancelled) setSlots(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [eventId, date, meta?.events, meta?.assignedTo?.id]);

  const counselorTz = meta?.timezone || 'Asia/Kolkata';

  const submit = async () => {
    if (!eventId || !date || !slot) {
      toast('error', 'Pick an event, date and time slot');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/leads/${leadId}/meetings`, {
        eventId,
        date,
        startTime: slot,
        // Book in counselor's TZ so the wall-clock time on their calendar
        // matches the slot they picked — not the admin's browser TZ.
        timezone: counselorTz,
        notes: notes.trim() || undefined,
      });
      toast('success', 'Meeting booked');
      onBooked();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to book meeting');
    } finally {
      setSubmitting(false);
    }
  };

  // Portalled to document.body so the drawer's stacking context is a
  // root-level sibling of every page chrome — z-index inside a nested
  // stacking context (like the lead-detail tab wrapper's z-0) can't
  // cover the tab strip above (z-10), and that mismatch showed the
  // WhatsApp / Calls tabs bleeding through the overlay.
  return createPortal(
    <div className="fixed inset-0 z-[100] flex justify-end bg-black/30">
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">Book Meeting</h3>
            {meta?.assignedTo?.name && (
              <p className="text-xs text-slate-500 mt-0.5">
                With <b>{meta.assignedTo.name}</b> · times in <b>{counselorTz}</b>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 rounded p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : !meta?.assignedTo ? (
            <EmptyState
              title="No counselor assigned"
              message="This lead has no assigned counselor. Assign one on the lead detail before booking a meeting."
            />
          ) : meta.events.length === 0 ? (
            <EmptyState
              title="No bookable events"
              message={`${meta.assignedTo.name} hasn't published any event on their Time Availability calendar. Ask them to create one, then try again.`}
            />
          ) : (
            <>
              {/* Event picker */}
              <div>
                <label className="block text-xs font-normal text-slate-600 mb-1.5">
                  Event Type <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {meta.events.map((e) => {
                    const active = e.id === eventId;
                    return (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => setEventId(e.id)}
                        className={`w-full text-left rounded-lg border transition-colors px-3 py-2.5 ${
                          active
                            ? 'border-[#6366F1] bg-indigo-50/50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
                          <span className="text-sm font-semibold text-slate-900">{e.name}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {e.duration} min
                          </span>
                          {e.location && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <MapPin className="h-3 w-3 flex-shrink-0" /> {e.location}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date picker */}
              <div>
                <label className="block text-xs font-normal text-slate-600 mb-1.5">
                  Date <span className="text-red-500">*</span>
                </label>
                <DateTimeInput
                  mode="date"
                  value={date}
                  min={todayInTz(counselorTz)}
                  onChange={setDate}
                  clearable={false}
                />
              </div>

              {/* Slot picker */}
              <div>
                <label className="block text-xs font-normal text-slate-600 mb-1.5">
                  Time Slot <span className="text-red-500">*</span>
                </label>
                {slotsLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  </div>
                ) : slots.length === 0 ? (
                  <p className="text-xs text-slate-400 py-3 text-center bg-slate-50 rounded-lg">
                    No slots available on this date. Try another day.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 max-h-52 overflow-y-auto">
                    {slots.map((s) => {
                      const active = s === slot;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setSlot(s)}
                          className={`rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${
                            active
                              ? 'border-[#6366F1] bg-[#6366F1] text-white'
                              : 'border-slate-200 text-slate-700 hover:border-slate-300'
                          }`}
                        >
                          {fmt12(s)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-normal text-slate-600 mb-1.5">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Anything the counselor should know before the meeting…"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40 resize-none"
                />
              </div>

              {/* Info footer — reminds admin that guest info auto-pulls */}
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-[11px] text-slate-500 flex items-start gap-2">
                <UserIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <p>
                  Guest name, email and phone are pulled from this lead automatically. Booking confirmation email and WhatsApp will use whichever templates you've picked in Settings → Communications.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !slot || !eventId || !meta?.assignedTo}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-semibold text-white shadow-sm"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Book Meeting
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className="text-center py-12">
      <CalendarIcon className="h-10 w-10 mx-auto text-slate-300 mb-3" />
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      <p className="text-xs text-slate-500 mt-1.5 max-w-xs mx-auto leading-relaxed">{message}</p>
    </div>
  );
}
